import { Col, Form, Input, Row } from 'antd'
import { FormikHelpers, FormikState } from 'formik'
import { NumericFormat } from 'react-number-format'
import { Trash } from 'iconsax-react'
import Card from 'components/card/Card'
import CardHeaderForm from 'components/card/CardHeaderForm'
import RequiredMark from 'components/form/RequiredMark'
import Button from 'components/button/Button'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import FormProductPriceValue, {
  FormPrice as PriceTierProps,
} from 'constants/interfaceFormik/productStockPrice'
import { rupiahFormat } from 'helpers/formatter'
import { classNameInput } from 'helpers/formik/classNameForm'

interface PriceProps {
  state: FormikState<FormProductPriceValue>
  helpers: FormikHelpers<FormProductPriceValue>
}

interface PriceError {
  minimumQuantity?: string
  maximumQuantity?: string
  price?: string
}

export default function Price(props: PriceProps) {
  const {
    helpers: { setFieldValue },
    state,
    state: { values, errors, touched },
  } = props

  const updateQuantity = (index: number, value: number) => {
    const newPrices = values.prices ? [...values.prices] : []
    newPrices[index].minimumQuantity = value ?? 0
    for (let i = 0; i < newPrices.length; i++) {
      if (i !== newPrices.length - 1) {
        newPrices[i].maximumQuantity = newPrices[i + 1].minimumQuantity - 1
      } else {
        newPrices[i].maximumQuantity = 0
      }
    }
    setFieldValue('prices', newPrices)
  }

  const removePriceTier = (priceInput: PriceTierProps) => {
    const prices = values.prices ? [...values.prices] : []
    const updatedPrices = prices.filter(price => price.id !== priceInput.id)

    for (let i = 0; i < updatedPrices.length; i++) {
      if (i !== updatedPrices.length - 1) {
        updatedPrices[i].maximumQuantity = updatedPrices[i + 1].minimumQuantity - 1
      } else {
        updatedPrices[i].maximumQuantity = 0
      }
    }
    setFieldValue('prices', updatedPrices)
  }

  return (
    <Card className="rounded-xl py-6 px-0" dataTestId="form_price">
      <CardHeaderForm title="2. Harga" />
      <Card.Body className="mt-0 px-6">
        <Form
          className="flex flex-col gap-3 xl:gap-9"
          layout="horizontal"
          preserve={false}
          colon={false}
          requiredMark={RequiredMark}
          labelCol={{ prefixCls: 'xl:w-3/12 font-bold' }}
          labelAlign="left"
          labelWrap
          wrapperCol={{ prefixCls: 'xl:w-9/12' }}
          style={{ maxWidth: '100%' }}
        >
          <Form.Item
            label={
              <div className="text-base font-medium text-[#323539] m-0 flex flex-col">
                <label>Konfigurasi Harga*</label>
                <span className="text-sm italic">(Maksimal 4 rentang Harga)</span>
              </div>
            }
            className="mb-0"
            wrapperCol={{ prefixCls: 'xl:flex xl:gap-6' }}
          >
            <Row gutter={[8, 8]} className="break-words">
              <Col span={2}></Col>
              <Col
                span={8}
                className="bg-[#FAFBFC] p-2 font-semibold text-base border border-r-0 border-solid border-[#E5E5E7] rounded-tl-lg text-[#333539]"
              >
                Jumlah Pesanan <span className="text-[#868C94] text-sm">/ Pcs</span>
              </Col>
              <Col
                span={8}
                className="bg-[#FAFBFC] p-2 font-semibold text-base border border-l-0 border-solid border-[#E5E5E7] text-[#333539]"
              >
                Harga <span className="text-[#868C94] text-sm">/ Pcs</span>
              </Col>
              <Col
                span={6}
                className="bg-[#FAFBFC] p-2 font-semibold text-base border border-l-0 border-solid border-[#E5E5E7] rounded-tr-lg text-[#333539]"
              >
                Preview <span className="text-[#868C94] text-sm">/ Pcs</span>
              </Col>
            </Row>
            <div>
              {values.prices &&
                values.prices.map((priceInput, index) => {
                  const classNameMinQuantity = classNameInput(
                    `prices[${index}].minimumQuantity`,
                    state,
                  )

                  const classNamePrice = classNameInput(`prices[${index}].price`, state)

                  return (
                    <Row key={priceInput.id} gutter={[8, 8]} data-testid="price_tier_input">
                      <Col lg={2} span={2} className="flex">
                        <span className="font-semibold text-2xl self-center">≥</span>
                      </Col>
                      <Col
                        lg={16}
                        span={16}
                        className="p-2 border border-y-0 border-solid border-[#E5E5E7]"
                      >
                        <Row gutter={[8, 8]}>
                          <Col lg={12}>
                            <div>
                              <NumericFormat
                                data-testid="input_minimumQuantity"
                                allowNegative={false}
                                className={`w-full ${classNameMinQuantity}`}
                                customInput={Input}
                                decimalScale={0}
                                decimalSeparator=","
                                placeholder="0"
                                thousandSeparator="."
                                status={
                                  errors.prices &&
                                  errors.prices[index] &&
                                  (errors.prices[index] as PriceError).minimumQuantity &&
                                  touched.prices
                                    ? 'error'
                                    : ''
                                }
                                value={
                                  priceInput.minimumQuantity ? priceInput.minimumQuantity : null
                                }
                                onValueChange={val => {
                                  updateQuantity(index, val.floatValue ?? 0)
                                }}
                              />
                              {errors.prices &&
                              errors.prices[index] &&
                              (errors.prices[index] as PriceError).minimumQuantity &&
                              touched.prices ? (
                                <ErrorHintForm
                                  errorMessage={
                                    (errors.prices[index] as PriceError).minimumQuantity as string
                                  }
                                  dataTestId="error-message-prices-minimumQuantity"
                                />
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div>
                              <div className="flex gap-2">
                                <NumericFormat
                                  data-testid="input_price"
                                  prefix="Rp "
                                  allowNegative={false}
                                  className={`w-full ${classNamePrice}`}
                                  customInput={Input}
                                  decimalScale={0}
                                  decimalSeparator=","
                                  placeholder="Rp 0"
                                  thousandSeparator="."
                                  status={
                                    errors.prices &&
                                    errors.prices[index] &&
                                    (errors.prices[index] as PriceError).price &&
                                    touched.prices
                                      ? 'error'
                                      : ''
                                  }
                                  value={priceInput.price ? priceInput.price : null}
                                  onValueChange={val => {
                                    setFieldValue(`prices[${index}].price`, val.floatValue ?? '')
                                  }}
                                />
                                {values.prices && values.prices.length > 1 && (
                                  <div className="flex">
                                    <Trash
                                      data-testid="button_removePriceTier"
                                      className="self-center"
                                      color="#FF4D4D"
                                      variant="Bold"
                                      onClick={() => {
                                        removePriceTier(priceInput)
                                      }}
                                    >
                                      remove
                                    </Trash>
                                  </div>
                                )}
                              </div>
                              {errors.prices &&
                              errors.prices[index] &&
                              (errors.prices[index] as PriceError).price &&
                              touched.prices ? (
                                <ErrorHintForm
                                  errorMessage={
                                    (errors.prices[index] as PriceError).price as string
                                  }
                                  dataTestId="error-message-prices-price"
                                />
                              ) : null}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col
                        lg={6}
                        span={6}
                        className="p-2 border-t-0 border-l-0 border-solid border-[#E5E5E7] break-words border-b border-r"
                      >
                        <div className="flex justify-between items-center h-full">
                          <span>
                            {priceInput.maximumQuantity
                              ? `${priceInput.minimumQuantity}-${priceInput.maximumQuantity}`
                              : `≥${priceInput.minimumQuantity}`}
                          </span>
                          {priceInput.price ? (
                            <span
                              className="text-sm text-[#3C5337] font-bold"
                              data-testid="text_price"
                            >
                              {rupiahFormat(priceInput.price)}
                            </span>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                  )
                })}
            </div>
            <Row gutter={[8, 8]}>
              <Col span={2} />
              <Col span={16} className="p-2 border border-solid border-[#E5E5E7] rounded-bl-lg">
                <div>
                  <Button
                    data-testid="button_addPriceTier"
                    variant="outline-primary"
                    className="pt-3 rounded-xl pb-3 font-bold text-sm"
                    disabled={values.prices && values.prices.length >= 4}
                    onClick={() => {
                      values.prices &&
                        setFieldValue('prices', [
                          ...values.prices,
                          {
                            id: Date.now(),
                            minimumQuantity: 0,
                            maximumQuantity: 0,
                            price: 0,
                          },
                        ])
                    }}
                  >
                    Tambah
                  </Button>
                </div>
              </Col>
              <Col
                span={6}
                className="p-2 border border-l-0 border-t-0 border-solid border-[#E5E5E7] roundedn-br-lg"
              />
            </Row>
          </Form.Item>
        </Form>
      </Card.Body>
    </Card>
  )
}
